/* 
This code establishes a shared state and functionality for controlling
the visibility of a sidebar across different components in a React 
application, using context and a custom hook.
*/

import React, { createContext, useContext, useState, ReactNode } from "react";

interface SidebarContextType {
  isExpanded: boolean;
  toggleSidebar: () => void;
}

/* 
React context for sidebar. 
Will hold SideBarContextType value. 
*/
const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

/* 
Custom hook to use SidebarContext. 
Convenient to access context's value in components. 
*/
export const useSidebar = (): SidebarContextType => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
};

interface SidebarProviderProps {
  children: ReactNode;
}

export const SidebarProvider: React.FC<SidebarProviderProps> = ({
  children,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  /* Function to toggle the sidebar's expanded state. */
  const toggleSidebar = () => {
    /* 
    Switches the value of isExpanded from
    true to false or vice-versa. 
    */
    setIsExpanded(!isExpanded);
  };

  return (
    /* 
    The Provider component makes the context value of isExpanded
    available to any child component that calls the useSidebar hook.
    */
    <SidebarContext.Provider value={{ isExpanded, toggleSidebar }}>
      {children}
    </SidebarContext.Provider>
  );
};
